import * as React from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { PopoverContent, PopoverTrigger, Popover } from '../UI/popover'
import FbButton from '../FbUI/FbButton'
import { cn } from '../UI/cn'
import { Calendar } from '../UI/calendar'

interface DatePickerProps {
  value?: string
  onChange: (value?: string) => void
  label?: string
  optional?: boolean
  className?: string
}

function formatYmd(date: Date): string {
  // Use UTC methods to avoid timezone issues
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseYmd(value?: string): Date | undefined {
  if (!value) return undefined
  const [y, m, d] = value.split('-').map((v) => parseInt(v, 10))
  if (!y || !m || !d) return undefined

  // Create date at noon UTC to avoid timezone boundary issues
  // This ensures the date doesn't shift when converting between timezones
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0))
  if (Number.isNaN(dt.getTime())) return undefined
  return dt
}

export function DatePicker({
  value,
  onChange,
  label,
  optional,
  className,
}: DatePickerProps) {
  const selected = React.useMemo(() => parseYmd(value), [value])
  const [open, setOpen] = React.useState(false)

  return (
    <div className={cn('flex flex-col mt-1 w-full', className)}>
      {label && (
        <label className="text-base font-medium text-gray-800 select-none mb-2">
          {label}
          {optional && (
            <span className="text-sm italic font-normal"> - optional</span>
          )}
        </label>
      )}
      <div className="grid gap-2 w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <FbButton
              variant="secondary"
              className={cn(
                'w-full justify-start text-left font-normal hover:text-black',
                !selected && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selected ? (
                selected.toLocaleDateString()
              ) : (
                <span>Pick a date</span>
              )}
            </FbButton>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="bg-white">
              <Calendar
                key={`calendar-${selected?.getTime() || 'no-date'}`}
                initialFocus
                mode="single"
                selected={selected}
                defaultMonth={selected || new Date()}
                onSelect={(dt) => {
                  onChange(dt ? formatYmd(dt) : undefined)
                  setOpen(false)
                }}
                numberOfMonths={1}
              />
              <div className="flex items-center justify-between px-4 pb-3">
                <button
                  type="button"
                  className="text-fb-dark-green hover:opacity-90 text-sm"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange('')
                    setOpen(false)
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-fb-dark-green hover:opacity-90 text-sm"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange(formatYmd(new Date()))
                    setOpen(false)
                  }}
                >
                  Today
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export default DatePicker
